import React from 'react';

import type { BoxProps, TabsProps as MuiTabsProps } from '@mui/material';
import { Box, Tab as MuiTab, Tabs as MuiTabs, tabClasses, tabsClasses } from '@mui/material';

import type { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

import type { TabChangeHandler, TabItem, TabItemKey } from './types';

type ControlledTabsProps =
  | {
      currentTabKey: TabItemKey;
      onChange: TabChangeHandler;
    }
  | {
      currentTabKey?: never;
      onChange?: TabChangeHandler;
    };

type TabsProps = Omit<MuiTabsProps, 'onChange'> & {
  tabs: TabItem[];
  containerProps?: BoxProps;
  additionalTabsContainerStyles?: SxProp;
  additionalTabStyles?: SxProp;
  contentStyles?: SxProp;
  /**
   * Keep every tab's panel mounted (inactive ones hidden via `React.Activity`)
   * so each panel's state survives switching away and back. Off by default.
   */
  keepMounted?: boolean;
  /** Tab to open on first render; the first tab otherwise. Uncontrolled mode only. */
  initialTabKey?: TabItemKey;
} & ControlledTabsProps;

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  currentTabKey: currentTabKeyExternal,
  onChange,
  containerProps,
  additionalTabsContainerStyles,
  additionalTabStyles,
  contentStyles,
  keepMounted = false,
  initialTabKey,
  ...props
}) => {
  const [currentTabKeyInternal, setCurrentTabKeyInternal] = React.useState<TabItemKey | undefined>(
    initialTabKey ?? tabs[0]?.key,
  );

  const currentTabKey = React.useMemo(() => {
    return currentTabKeyExternal || currentTabKeyInternal;
  }, [currentTabKeyExternal, currentTabKeyInternal]);

  const currentTab = React.useMemo(() => {
    return tabs.find(tab => tab.key === currentTabKey);
  }, [currentTabKey, tabs]);

  const handleChangeTab: NonNullable<MuiTabsProps['onChange']> = React.useCallback(
    (_event, tabkey) => {
      if (!currentTabKeyExternal) {
        setCurrentTabKeyInternal(tabkey);
      }
      onChange?.(tabkey);
    },
    [currentTabKeyExternal, onChange],
  );

  return (
    <Box {...containerProps}>
      <MuiTabs
        onChange={handleChangeTab}
        value={currentTabKey}
        variant="fullWidth"
        sx={composeSx(initialTabsContainerStyles, additionalTabsContainerStyles)}
        {...props}
      >
        {tabs.map(tab => (
          <MuiTab
            key={tab.key}
            value={tab.key}
            label={tab.label}
            sx={composeSx(initialTabStyles, additionalTabStyles)}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </MuiTabs>
      <Box sx={contentStyles}>
        {keepMounted
          ? tabs.map(tab => (
              <React.Activity key={tab.key} mode={tab.key === currentTabKey ? 'visible' : 'hidden'}>
                {tab.component}
              </React.Activity>
            ))
          : currentTab?.component}
      </Box>
    </Box>
  );
};

const TABS_HEIGHT = 32; // in pixels

const initialTabsContainerStyles: SxProp = theme => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  minHeight: TABS_HEIGHT,
  [`& .${tabsClasses.indicator}`]: {
    display: 'none',
  },
  margin: '0px 0 24px',
  borderRadius: 1,
});

const initialTabStyles: SxProp = theme => ({
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.text.primary,
  textTransform: 'none',
  minHeight: TABS_HEIGHT,
  padding: 0,
  '&:not(:last-of-type)': {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  [`&.${tabClasses.selected}`]: {
    color: 'inherit',
    backgroundColor: theme.palette.action.focus,
  },
});
